import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import { ITag, TagType } from "dto/ITag";
import tagsService from "api/tagsService";
import Input from "components/Input";
import TagBadge from "components/TagBadge";
import { EViews } from "../ClassificationTab/typings";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

// ── Demo-etykiety dla pustych zakładek ───────────────────────────────

const DEMO_TAGS: Record<string, ITag[]> = {
  [EViews.CASES]: [
    { Id: "demo-c1", Name: "Pilna",       Type: TagType.Cases, Flags: 0, Global: true },
    { Id: "demo-c2", Name: "Ważna",       Type: TagType.Cases, Flags: 0, Global: true },
    { Id: "demo-c3", Name: "Do weryfikacji", Type: TagType.Cases, Flags: 0, Global: true },
    { Id: "demo-c4", Name: "Archiwalna",  Type: TagType.Cases, Flags: 0, Global: true },
  ],
  [EViews.ACTIONS]: [
    { Id: "demo-a1", Name: "Sprint",      Type: TagType.Tasks, Flags: 0, Global: true },
    { Id: "demo-a2", Name: "Backlog",     Type: TagType.Tasks, Flags: 0, Global: true },
    { Id: "demo-a3", Name: "Pilne",       Type: TagType.Tasks, Flags: 0, Global: true },
    { Id: "demo-a4", Name: "Blokada",     Type: TagType.Tasks, Flags: 0, Global: true },
  ],
  [EViews.EVENTS]: [
    { Id: "demo-e1", Name: "Rozprawa",    Type: TagType.Events, Flags: 0, Global: true },
    { Id: "demo-e2", Name: "Spotkanie",   Type: TagType.Events, Flags: 0, Global: true },
    { Id: "demo-e3", Name: "Termin",      Type: TagType.Events, Flags: 0, Global: true },
    { Id: "demo-e4", Name: "Szkolenie",   Type: TagType.Events, Flags: 0, Global: true },
  ],
  [EViews.LETTERS]: [
    { Id: "demo-l1", Name: "Pozew",       Type: TagType.EditorDocuments, Flags: 0, Global: true },
    { Id: "demo-l2", Name: "Odpowiedź",   Type: TagType.EditorDocuments, Flags: 0, Global: true },
    { Id: "demo-l3", Name: "Wniosek",     Type: TagType.EditorDocuments, Flags: 0, Global: true },
    { Id: "demo-l4", Name: "Ugoda",       Type: TagType.EditorDocuments, Flags: 0, Global: true },
  ],
  [EViews.PACKAGES_INCOMING]: [
    { Id: "demo-pi1", Name: "Sąd",        Type: TagType.Incoming, Flags: 0, Global: true },
    { Id: "demo-pi2", Name: "Klient",     Type: TagType.Incoming, Flags: 0, Global: true },
    { Id: "demo-pi3", Name: "Urzędowe",   Type: TagType.Incoming, Flags: 0, Global: true },
    { Id: "demo-pi4", Name: "Pilne",      Type: TagType.Incoming, Flags: 0, Global: true },
  ],
  [EViews.PACKAGES_OUTCOMING]: [
    { Id: "demo-po1", Name: "Sąd",        Type: TagType.Outgoing, Flags: 0, Global: true },
    { Id: "demo-po2", Name: "Klient",     Type: TagType.Outgoing, Flags: 0, Global: true },
    { Id: "demo-po3", Name: "Urzędowe",   Type: TagType.Outgoing, Flags: 0, Global: true },
    { Id: "demo-po4", Name: "Polecony",   Type: TagType.Outgoing, Flags: 0, Global: true },
  ],
  [EViews.TEMPLATES]: [
    { Id: "demo-t1", Name: "Cywilne",     Type: TagType.Templates, Flags: 0, Global: true },
    { Id: "demo-t2", Name: "Karne",       Type: TagType.Templates, Flags: 0, Global: true },
    { Id: "demo-t3", Name: "Administracyjne", Type: TagType.Templates, Flags: 0, Global: true },
    { Id: "demo-t4", Name: "Pracy",       Type: TagType.Templates, Flags: 0, Global: true },
  ],
  [EViews.FOLDERS]: [
    { Id: "demo-f1", Name: "Dokumenty",   Type: TagType.Folder, Flags: 0, Global: true },
    { Id: "demo-f2", Name: "Pisma",       Type: TagType.Folder, Flags: 0, Global: true },
    { Id: "demo-f3", Name: "Archiwum",    Type: TagType.Folder, Flags: 0, Global: true },
    { Id: "demo-f4", Name: "Aktywne",     Type: TagType.Folder, Flags: 0, Global: true },
  ],
};

// ── Główny komponent ─────────────────────────────────────────────────

const ClassificationDefault: FC<Props> = ({ myPrivilages, type }) => {
  const { t } = useTranslation();
  const workspaceId = getWorkspaceId();
  const [tagType, setTagType] = useState<TagType>(null);

  const { data: tagsData, refetch, isSuccess: userLoaded } =
    tagsService.useGetTags(tagType === null, tagType);
  const { data: systemTagsData, refetch: refetchSystem, isSuccess: sysLoaded } =
    tagsService.useGetTags(tagType === null, tagType, workspaceId ?? "");
  const { mutate: addTag } = tagsService.useCreateTag();
  const { mutate: removeTag } = tagsService.useDeleteTag();
  const { mutate: editTag } = tagsService.useEditTag();

  const [tagValue, setTagValue] = useState<string>();

  useEffect(() => {
    switch (type) {
      case EViews.CASES:             setTagType(TagType.Cases);           return;
      case EViews.ACTIONS:           setTagType(TagType.Tasks);           return;
      case EViews.EVENTS:            setTagType(TagType.Events);          return;
      case EViews.CLIENTS:           setTagType(TagType.Participants);    return;
      case EViews.LETTERS:           setTagType(TagType.EditorDocuments); return;
      case EViews.PACKAGES_INCOMING: setTagType(TagType.Incoming);        return;
      case EViews.PACKAGES_OUTCOMING:setTagType(TagType.Outgoing);        return;
      case EViews.TEMPLATES:         setTagType(TagType.Templates);       return;
      case EViews.FOLDERS:           setTagType(TagType.Folder);          return;
      default:                        setTagType(null);                    return;
    }
  }, [type]);

  const rawTags       = useMemo(() => tagsData?.data      ?? [], [tagsData]);
  const rawSystemTags = useMemo(() => systemTagsData?.data ?? [], [systemTagsData]);

  // ── Deduplikowane połączenie etykiet ──────────────────────────────
  const allTags = useMemo(() => {
    const seen = new Set<string>();
    const dedup = (items: ITag[]) =>
      items.filter((t) => {
        if (seen.has(t.Id)) return false;
        seen.add(t.Id);
        return true;
      });
    const real = dedup([
      ...rawSystemTags,
      ...rawTags.filter((t) => !t.WorkspaceId && !t.Global),
    ]);
    if (sysLoaded && userLoaded && real.length === 0) {
      return DEMO_TAGS[type] ?? [];
    }
    return real;
  }, [rawSystemTags, rawTags, sysLoaded, userLoaded, type]);

  const isDemo = (tag: ITag) => tag.Id.startsWith("demo-");

  // ── Tooltip (localStorage) ────────────────────────────────────────
  const [tooltipMap, setTooltipMap] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem("tag_tooltips_v1") || "{}"); }
    catch { return {}; }
  });
  const [editingTooltipId, setEditingTooltipId] = useState<string | null>(null);
  const [tooltipDraft, setTooltipDraft] = useState("");

  const startEditTooltip = (tag: ITag) => {
    setEditingTooltipId(tag.Id);
    setTooltipDraft(tooltipMap[tag.Id] ?? tag.Name);
  };

  const saveTooltip = (tagId: string) => {
    setTooltipMap((prev) => {
      const next = { ...prev, [tagId]: tooltipDraft };
      localStorage.setItem("tag_tooltips_v1", JSON.stringify(next));
      return next;
    });
    setEditingTooltipId(null);
  };

  const cancelTooltip = () => setEditingTooltipId(null);

  // ── Inline edit nazwy chipa ───────────────────────────────────────
  const [editingChipId, setEditingChipId] = useState<string | null>(null);
  const [chipDraft, setChipDraft] = useState("");

  const startEditChip = (tag: ITag) => {
    if (isDemo(tag)) return;
    setEditingChipId(tag.Id);
    setChipDraft(tag.Name);
  };

  const saveChip = (tag: ITag) => {
    if (!chipDraft.trim()) return;
    editTag(
      { Id: tag.Id, Name: chipDraft.trim() },
      {
        onSuccess: () => {
          refetch();
          refetchSystem();
          setEditingChipId(null);
        },
      },
    );
  };

  const cancelChip = () => setEditingChipId(null);

  // ── Usuwanie ──────────────────────────────────────────────────────
  const handleRemoveTag = (tag: ITag) => {
    if (isDemo(tag)) return;
    removeTag(
      { Id: tag.Id },
      { onSuccess: () => { refetch(); refetchSystem(); } },
    );
  };

  // ── Dodawanie ─────────────────────────────────────────────────────
  const handleAddTag = () => {
    if (!tagValue) return;
    addTag(
      { Name: tagValue, Type: tagType },
      { onSuccess: () => { refetch(); setTagValue(""); } },
    );
  };

  // ── DnD (kolejność per typ) ───────────────────────────────────────
  const getOrderForType = (t: string): string[] => {
    try {
      const stored = JSON.parse(localStorage.getItem("tag_order_v1") || "{}");
      return Array.isArray(stored[t]) ? stored[t] : [];
    } catch { return []; }
  };

  const [orderedIds, setOrderedIds] = useState<string[]>(() => getOrderForType(type));

  useEffect(() => {
    setOrderedIds(getOrderForType(type));
  }, [type]);

  const sortedTags = useMemo(() => {
    if (orderedIds.length === 0) return allTags;
    const indexed = new Map(allTags.map((t) => [t.Id, t]));
    const ordered = orderedIds.map((id) => indexed.get(id)).filter(Boolean) as ITag[];
    const rest = allTags.filter((t) => !orderedIds.includes(t.Id));
    return [...ordered, ...rest];
  }, [allTags, orderedIds]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(sortedTags);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const newOrder = items.map((t) => t.Id);
    setOrderedIds(newOrder);
    try {
      const stored = JSON.parse(localStorage.getItem("tag_order_v1") || "{}");
      stored[type] = newOrder;
      localStorage.setItem("tag_order_v1", JSON.stringify(stored));
    } catch {}
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className={styles.tableWrapper}>

      {/* Tabela etykiet */}
      <div className={styles.statusTable}>

        {/* Nagłówek */}
        <div className={styles.tableHeader}>
          <span className={styles.colDrag} />
          <span className={styles.colChip}>Chip</span>
          <span className={styles.colTooltip}>Tooltip</span>
        </div>

        {/* Wiersze z DnD */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tag-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {sortedTags.map((tag, index) => {
                  const isEditingChip    = editingChipId    === tag.Id;
                  const isEditingTooltip = editingTooltipId === tag.Id;
                  const tooltipText      = tooltipMap[tag.Id] ?? tag.Name;

                  return (
                    <Draggable key={tag.Id} draggableId={tag.Id} index={index}>
                      {(drag, snapshot) => (
                        <div
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          className={`${styles.tableRow} ${snapshot.isDragging ? styles.tableRowDragging : ""}`}
                        >
                          {/* Drag handle */}
                          <span className={styles.colDrag} {...drag.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} className={styles.dragHandle} />
                          </span>

                          {/* Chip */}
                          <span className={styles.colChip}>
                            {isEditingChip ? (
                              <div className={styles.chipEditWrap}>
                                <input
                                  autoFocus
                                  className={styles.chipInput}
                                  value={chipDraft}
                                  onChange={(e) => setChipDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveChip(tag);
                                    if (e.key === "Escape") cancelChip();
                                  }}
                                />
                                <button className={styles.saveBtn} onClick={() => saveChip(tag)}>✓</button>
                                <button className={styles.cancelBtn} onClick={cancelChip}>✕</button>
                              </div>
                            ) : (
                              <span
                                className={`${styles.chipClickable} ${isDemo(tag) ? styles.chipDemo : ""}`}
                                onClick={() => startEditChip(tag)}
                                title={isDemo(tag) ? undefined : "Kliknij aby edytować"}
                              >
                                <TagBadge
                                  tag={tag}
                                  onRemove={!tag.Global && !isDemo(tag) ? () => handleRemoveTag(tag) : undefined}
                                />
                              </span>
                            )}
                          </span>

                          {/* Tooltip */}
                          <span className={styles.colTooltip}>
                            {isEditingTooltip ? (
                              <div className={styles.tooltipEditWrap}>
                                <input
                                  autoFocus
                                  className={styles.tooltipInput}
                                  value={tooltipDraft}
                                  onChange={(e) => setTooltipDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveTooltip(tag.Id);
                                    if (e.key === "Escape") cancelTooltip();
                                  }}
                                />
                                <button className={styles.saveBtn} onClick={() => saveTooltip(tag.Id)}>✓</button>
                                <button className={styles.cancelBtn} onClick={cancelTooltip}>✕</button>
                              </div>
                            ) : (
                              <span
                                className={styles.tooltipText}
                                onClick={() => startEditTooltip(tag)}
                                title="Kliknij aby edytować"
                              >
                                {tooltipText}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Wiersz dodawania */}
        <div className={styles.tableAddRow}>
          <span className={styles.colDrag} />
          <span className={styles.colChip}>
            <Input
              type="text"
              onChange={(text) => setTagValue(text)}
              onAccept={handleAddTag}
              label={t("tags.tagName")}
              onRightText={t("common.save")}
              onRightClick={handleAddTag}
              value={tagValue}
              hideTags
            />
          </span>
          <span className={styles.colTooltip} />
        </div>

      </div>
    </div>
  );
};

export default ClassificationDefault;
